import * as React from "react";
import { ChevronDown } from "lucide-react";

import { cn } from "../../lib/utils";

export enum LinkVariant {
	Primary = "primary",
	Secondary = "secondary",
	Tertiary = "tertiary",
	Neutral = "neutral",
	Success = "success",
	Danger = "danger",
	Black = "black",
	AllWhite = "all-white",
	InvertedPrimary = "inverted-primary",
	InvertedSecondary = "inverted-secondary",
	NavInverted = "nav",
	None = "no-variant",
}

const variantClasses: Record<LinkVariant, string> = {
	[LinkVariant.Primary]:
		"text-primary-400 hover:text-primary-500 focus:text-primary-500",
	[LinkVariant.Secondary]:
		"text-secondary hover:text-primary focus:text-primary",
	[LinkVariant.Tertiary]:
		"text-secondary hover:text-primary focus:text-primary",
	[LinkVariant.Neutral]:
		"text-neutral-500 hover:text-primary focus:text-primary",
	[LinkVariant.Success]:
		"text-success hover:text-success-500 focus:text-success-500",
	[LinkVariant.Danger]:
		"text-danger hover:text-danger-500 focus:text-danger-500",
	[LinkVariant.Black]: "text-neutral-800 hover:text-primary focus:text-primary",
	[LinkVariant.AllWhite]: "text-white hover:text-white/50 focus:text-white/50",
	[LinkVariant.InvertedPrimary]:
		"text-white hover:text-neutral-200 focus:text-neutral-200",
	[LinkVariant.InvertedSecondary]:
		"text-neutral-200 hover:text-white focus:text-white",
	[LinkVariant.NavInverted]: "text-white/80 hover:text-white focus:text-white",
	[LinkVariant.None]: "",
};

export type LinkProps<C extends React.ElementType = "a"> = {
	as?: C;
	variant?: LinkVariant;
	isDisabled?: boolean;
	hasChevron?: boolean;
	icon?: React.ReactNode;
	className?: string;
	children?: React.ReactNode;
} & Omit<React.ComponentPropsWithoutRef<C>, "as" | "className" | "children">;

function LinkImpl<C extends React.ElementType = "a">(
	{
		as,
		variant = LinkVariant.Primary,
		isDisabled = false,
		hasChevron = false,
		icon,
		className,
		children,
		onClick,
		...props
	}: LinkProps<C>,
	ref: React.Ref<Element>,
) {
	const Comp = (as ?? "a") as React.ElementType;
	const hasIcon = !!icon || hasChevron;

	return (
		<Comp
			ref={ref}
			onClick={onClick}
			className={cn(
				"group transition-colors duration-75 focus:underline focus:outline-none",
				onClick && "cursor-pointer",
				hasIcon && "inline-flex items-center gap-2",
				isDisabled && "pointer-events-none cursor-not-allowed text-neutral-300",
				!isDisabled && variantClasses[variant],
				className,
			)}
			{...props}
		>
			{icon}
			{children}
			{hasChevron && (
				<ChevronDown
					className="size-4 -rotate-90 transition-transform duration-200 group-hover:translate-x-[3px]"
					data-testid="link-chevron"
				/>
			)}
		</Comp>
	);
}

export const Link = React.forwardRef(LinkImpl) as <
	C extends React.ElementType = "a",
>(
	props: LinkProps<C> & { ref?: React.Ref<Element> },
) => React.ReactElement | null;
